import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Loading, Empty, SectionHeader, Card, SupportBadge, SUPPORT_STATUSES, SUPPORT_LABEL } from "./shared";

export default function AdminSupport() {
  const [orgs, setOrgs] = useState(null);
  const [draft, setDraft] = useState({}); // id -> {status, note}
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/organizations").then((r) => {
      setOrgs(r.data.organizations || []);
      const d = {};
      (r.data.organizations || []).forEach((o) => { d[o.id] = { status: o.support.status, note: o.support.note || "" }; });
      setDraft(d);
    }).catch(() => setOrgs([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (orgs === null) return <Loading />;

  const setField = (id, field, value) => setDraft((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const apply = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/organizations/${confirm.org.id}/support`, { status: confirm.status, note: confirm.note });
      toast.success(`Statut support mis à jour · ${confirm.org.name}`);
      setConfirm(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-support">
      <SectionHeader title="Support Status" subtitle="Suivi du statut support par organisation." />
      {orgs.length === 0 ? <Empty label="Aucune organisation." testid="support-empty" /> : (
        <div className="space-y-3">
          {orgs.map((o) => {
            const dr = draft[o.id] || {};
            const changed = dr.status !== o.support.status || (dr.note || "") !== (o.support.note || "");
            return (
              <Card key={o.id} className="p-4" data-testid={`support-row-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-[#17201A]">{o.name}</p>
                    <SupportBadge status={o.support.status} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={dr.status} onValueChange={(v) => setField(o.id, "status", v)}>
                      <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`support-status-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SUPPORT_STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{SUPPORT_LABEL[s]}</SelectItem>)}</SelectContent>
                    </Select>
                    <button data-testid={`support-save-${o.id}`} disabled={!changed}
                      onClick={() => setConfirm({ org: o, status: dr.status, note: dr.note })}
                      className="rounded-lg bg-[#1E3D2B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2C5540] disabled:opacity-40">
                      Enregistrer
                    </button>
                  </div>
                </div>
                <Textarea data-testid={`support-note-${o.id}`} value={dr.note} onChange={(e) => setField(o.id, "note", e.target.value)}
                  placeholder="Note interne (optionnel)…" className="mt-3 min-h-[60px] text-sm" />
              </Card>
            );
          })}
        </div>
      )}
      <ConfirmDialog
        open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}
        title="Modifier le statut support"
        description={confirm ? `Confirmer le statut « ${SUPPORT_LABEL[confirm.status]} » pour « ${confirm.org.name} » ? Cette action est journalisée.` : ""}
        confirmLabel="Confirmer" destructive={confirm?.status === "critical"}
        loading={saving} onConfirm={apply}
      />
    </div>
  );
}
